import React, { useCallback, useMemo } from 'react'
import styled from '@emotion/styled'
import { Table, Tag, Tooltip, notification, Card, Button, Select, Space } from 'antd'
import { createSimpleStore } from 'react-simple-reducer'
import { CupomModel } from '../../models/CupomModel'
import { CursoModel } from '../../models/CursoModel'
import { ParceirosModel } from '../../models/ParceirosModel'
import axios from '../../api'
import { differenceInCalendarDays, format } from 'date-fns'
import { Link, useHistory } from 'react-router-dom'

type VigenciaStatus = 'vigente' | 'programado' | 'expirado' | 'sem_vigencia'
type VigenciaFilter = 'all' | VigenciaStatus
type ParceiroFilter = number | '__none__' | null

const UFS = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
]

export const CuponsView = () => {
  const init = useCallback((dispatch) => {
    dispatch(Store.thunks.getCursosCatalogo())
    dispatch(Store.thunks.getParceiros())
    dispatch(Store.thunks.getCupons())
  }, [])

  return (
    <Layout>
      <Store.Provider init={init}>
        <CuponsViewHeader />
        <FiltersSection />
        <ResultsCard>
          <TableWrapper>
            <CuponsTable />
          </TableWrapper>
        </ResultsCard>
      </Store.Provider>
    </Layout>
  )
}

const CuponsViewHeader = () => {
  const history = useHistory()

  return (
    <PageHeader>
      <Title>Cupons</Title>
      <Button type="primary" onClick={() => history.push('/cupons/novo')}>
        Criar novo cupom
      </Button>
    </PageHeader>
  )
}

const FiltersSection = () => {
  const dispatch = Store.useDispatch()
  const { filters, cursosCatalogo, parceiros } = Store.useState()

  const handleVigenciaChange = (value: VigenciaFilter | undefined) => {
    dispatch(Store.thunks.getCupons({ vigencia: value ?? 'all' }, { page: 1 }))
  }

  const handleCursosChange = (value: number[]) => {
    dispatch(Store.thunks.getCupons({ cursoIds: value ?? [] }, { page: 1 }))
  }

  const handleUfsChange = (value: string[]) => {
    dispatch(Store.thunks.getCupons({ ufs: value ?? [] }, { page: 1 }))
  }

  const handleParceiroChange = (value: number | '__none__' | undefined) => {
    dispatch(Store.thunks.getCupons({ parceiroId: value ?? null }, { page: 1 }))
  }

  return (
    <FiltersContainer>
      <Space size="middle" wrap>
        <Select
          style={{ width: 180 }}
          value={filters.vigencia}
          onChange={handleVigenciaChange}
          options={[
            { value: 'all', label: 'Todas vigências' },
            { value: 'vigente', label: 'Vigente' },
            { value: 'programado', label: 'Programado' },
            { value: 'expirado', label: 'Expirado' },
            { value: 'sem_vigencia', label: 'Sem vigência' },
          ]}
        />

        <Select
          mode="multiple"
          allowClear
          placeholder="Curso"
          style={{ width: 260 }}
          value={filters.cursoIds}
          onChange={handleCursosChange}
          showSearch
          optionFilterProp="label"
          options={cursosCatalogo.map((curso) => ({
            value: curso.id,
            label: curso.nomeReduzido || curso.nome,
          }))}
        />

        <Select
          mode="multiple"
          allowClear
          placeholder="UF"
          style={{ width: 200 }}
          value={filters.ufs}
          onChange={handleUfsChange}
          options={UFS.map((uf) => ({ value: uf, label: uf }))}
        />

        <Select
          allowClear
          placeholder="Parceiro"
          style={{ width: 260 }}
          value={filters.parceiroId ?? undefined}
          onChange={handleParceiroChange}
          showSearch
          optionFilterProp="label"
          options={[
            { value: '__none__', label: 'Sem parceiro' },
            ...parceiros.map((parceiro) => ({
              value: parceiro.id,
              label: parceiro.nome,
            })),
          ]}
        />
      </Space>
    </FiltersContainer>
  )
}

const CuponsTable = () => {
  const { cupons, loading, cursosCatalogo, pagination } = Store.useState()
  const dispatch = Store.useDispatch()

  const cursoNomePorId = useMemo(() => {
    const map = new Map<number, string>()
    cursosCatalogo.forEach((curso) => {
      map.set(curso.id, curso.nomeReduzido || curso.nome)
    })
    return map
  }, [cursosCatalogo])

  const columns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 180,
      render: (codigo: string, cupom: CupomModel) => (
        <Link to={`/cupons/${cupom.id}`}>{codigo}</Link>
      ),
    },
    {
      title: 'Desconto',
      key: 'desconto',
      width: 120,
      render: (_: unknown, cupom: CupomModel) => <DiscountTag cupom={cupom} />,
    },
    {
      title: 'Escopo',
      key: 'escopo',
      width: 220,
      render: (_: unknown, cupom: CupomModel) => (
        <ScopeSummary cupom={cupom} cursoNomePorId={cursoNomePorId} />
      ),
    },
    {
      title: 'Usos',
      key: 'usos',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, cupom: CupomModel) => cupom.pagamentos?.length ?? 0,
    },
    {
      title: 'Parceiro',
      key: 'parceiro',
      width: 220,
      render: (_: unknown, cupom: CupomModel) => cupom.parceiro?.nome ?? 'Sem parceiro',
    },
    {
      title: 'Vigência',
      key: 'vigencia',
      width: 120,
      render: (_: unknown, cupom: CupomModel) => <Vigencia cupom={cupom} />,
    },
    {
      title: 'Criado em',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (createdAt: string) => format(new Date(createdAt), 'dd/MM/yyyy'),
    },
  ]

  const handleTableChange = (tablePagination: any) => {
    dispatch(
      Store.thunks.getCupons(
        {},
        {
          page: tablePagination.current ?? pagination.page,
          limit: tablePagination.pageSize ?? pagination.limit,
        }
      )
    )
  }

  return (
    <StyledTable
      columns={columns}
      dataSource={cupons}
      loading={loading}
      rowKey="id"
      scroll={{ x: 1100 }}
      pagination={{
        current: pagination.page,
        pageSize: pagination.limit,
        total: pagination.total,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
      }}
      onChange={handleTableChange}
      locale={{ emptyText: 'Não há nenhum cupom cadastrado ainda.' }}
    />
  )
}

function DiscountTag({ cupom }: { cupom: CupomModel }) {
  const formatNumber = (value?: number | null) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(value ?? 0)

  if (cupom.valorDesconto != null) {
    return <DiscountValueTag color="green">{formatNumber(cupom.valorDesconto)}</DiscountValueTag>
  }

  if (cupom.percentualDesconto != null) {
    return <DiscountValueTag color="orange">{cupom.percentualDesconto}% OFF</DiscountValueTag>
  }

  return <DiscountValueTag color="default">Sem desconto</DiscountValueTag>
}

function ScopeSummary({
  cupom,
  cursoNomePorId,
}: {
  cupom: CupomModel
  cursoNomePorId: Map<number, string>
}) {
  const cursoIds = normalizeCourseIds(cupom.cursos)
  const cursosCount = cursoIds.length
  const ufs = cupom.ufs ?? []
  const ufsCount = ufs.length

  const cursosText = cursosCount === 0 ? 'Todos os cursos' : `${cursosCount} curso(s)`
  const ufsText = ufsCount === 0 ? 'Todas as UFs' : `${ufsCount} UF(s)`

  const cursosNomes = cursoIds.map((cursoId) => cursoNomePorId.get(cursoId) ?? `ID ${cursoId}`)

  return (
    <ScopeContainer>
      <Tooltip
        placement="topLeft"
        title={
          <ScopeTooltipContent>
            <ScopeGroupTitle>Cursos</ScopeGroupTitle>
            {cursosCount === 0 ? (
              <ScopeHint>Todos os cursos</ScopeHint>
            ) : (
              <ScopeList>{cursosNomes.join(', ')}</ScopeList>
            )}
          </ScopeTooltipContent>
        }
      >
        <ScopeTagContainer>
          <ScopeValueTag color={cursosCount === 0 ? 'default' : 'blue'}>{cursosText}</ScopeValueTag>
        </ScopeTagContainer>
      </Tooltip>
      <Tooltip
        placement="topLeft"
        title={
          <ScopeTooltipContent>
            <ScopeGroupTitle>UFs</ScopeGroupTitle>
            {ufsCount === 0 ? (
              <ScopeHint>Todas as UFs</ScopeHint>
            ) : (
              <ScopeList>{ufs.join(', ')}</ScopeList>
            )}
          </ScopeTooltipContent>
        }
      >
        <ScopeTagContainer>
          <ScopeValueTag color={ufsCount === 0 ? 'default' : 'purple'}>{ufsText}</ScopeValueTag>
        </ScopeTagContainer>
      </Tooltip>
    </ScopeContainer>
  )
}

function Vigencia({ cupom }: { cupom: CupomModel }) {
  const startDate = cupom.dataInicioVigencia ? new Date(cupom.dataInicioVigencia) : null
  const endDate = cupom.dataFimVigencia ? new Date(cupom.dataFimVigencia) : null

  const { status, statusLabel, periodLabel, relativeLabel } = resolveVigenciaDisplay(
    startDate,
    endDate
  )
  const showTooltip = periodLabel !== 'Sem período definido' || !!relativeLabel

  return (
    <VigenciaContainer>
      <Tooltip
        title={
          <VigenciaTooltipContent>
            <div>{periodLabel}</div>
            {relativeLabel ? (
              <VigenciaTooltipSecondary>{relativeLabel}</VigenciaTooltipSecondary>
            ) : null}
          </VigenciaTooltipContent>
        }
        placement="topLeft"
        mouseEnterDelay={0.2}
        open={showTooltip ? undefined : false}
      >
        <VigenciaTag color={mapStatusColor(status)}>{statusLabel}</VigenciaTag>
      </Tooltip>
    </VigenciaContainer>
  )
}

function resolveVigenciaDisplay(startDate: Date | null, endDate: Date | null) {
  const periodLabel = buildPeriodText(startDate, endDate) ?? 'Sem período definido'

  if (!startDate && !endDate) {
    return {
      status: 'sem_vigencia' as VigenciaStatus,
      statusLabel: 'Sem vigência',
      periodLabel,
      relativeLabel: null as string | null,
    }
  }

  const now = new Date()
  const nowDayKey = getSaoPauloDayKey(now)
  const startDayKey = startDate ? getSaoPauloDayKey(startDate) : null
  const endDayKey = endDate ? getSaoPauloDayKey(endDate) : null

  if (startDayKey && startDayKey > nowDayKey) {
    const daysUntilStart = Math.max(
      1,
      differenceInCalendarDays(parseDayKeyToUtcDate(startDayKey), parseDayKeyToUtcDate(nowDayKey))
    )
    return {
      status: 'programado' as VigenciaStatus,
      statusLabel: 'Programado',
      periodLabel,
      relativeLabel: `Começa em ${daysUntilStart} ${pluralizeDay(daysUntilStart)}`,
    }
  }

  if (endDayKey && endDayKey < nowDayKey) {
    const daysSinceEnd = Math.max(
      1,
      differenceInCalendarDays(parseDayKeyToUtcDate(nowDayKey), parseDayKeyToUtcDate(endDayKey))
    )
    return {
      status: 'expirado' as VigenciaStatus,
      statusLabel: 'Expirado',
      periodLabel,
      relativeLabel: `Encerrou há ${daysSinceEnd} ${pluralizeDay(daysSinceEnd)}`,
    }
  }

  if (endDayKey) {
    const daysUntilEnd = Math.max(
      0,
      differenceInCalendarDays(parseDayKeyToUtcDate(endDayKey), parseDayKeyToUtcDate(nowDayKey))
    )
    return {
      status: 'vigente' as VigenciaStatus,
      statusLabel: 'Vigente',
      periodLabel,
      relativeLabel: `Termina em ${daysUntilEnd} ${pluralizeDay(daysUntilEnd)}`,
    }
  }

  return {
    status: 'vigente' as VigenciaStatus,
    statusLabel: 'Vigente',
    periodLabel,
    relativeLabel: null,
  }
}

function mapStatusColor(status: VigenciaStatus) {
  if (status === 'programado') return '#1677ff'
  if (status === 'expirado') return '#ff4d4f'
  if (status === 'vigente') return '#52c41a'
  return '#8c8c8c'
}

function pluralizeDay(amountOfDays: number) {
  return amountOfDays === 1 ? 'dia' : 'dias'
}

function buildPeriodText(startDate: Date | null, endDate: Date | null) {
  if (startDate && endDate) {
    return `De ${format(startDate, 'dd/MM/yyyy')} até ${format(endDate, 'dd/MM/yyyy')}`
  }

  if (startDate) {
    return `A partir de ${format(startDate, 'dd/MM/yyyy')}`
  }

  if (endDate) {
    return `Até ${format(endDate, 'dd/MM/yyyy')}`
  }

  return null
}

function getSaoPauloDayKey(date: Date): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  const parts = formatter.formatToParts(date)
  const year = parts.find((part) => part.type === 'year')?.value
  const month = parts.find((part) => part.type === 'month')?.value
  const day = parts.find((part) => part.type === 'day')?.value
  return `${year}-${month}-${day}`
}

function parseDayKeyToUtcDate(dayKey: string): Date {
  return new Date(`${dayKey}T00:00:00.000Z`)
}

function normalizeCourseIds(cursos: unknown): number[] {
  if (!Array.isArray(cursos)) return []

  const normalized = cursos
    .map((curso) => {
      if (typeof curso === 'number' || typeof curso === 'string') return Number(curso)
      if (curso && typeof curso === 'object' && 'id' in curso) return Number((curso as any).id)
      return Number.NaN
    })
    .filter((cursoId) => Number.isFinite(cursoId))

  return Array.from(new Set(normalized))
}

const Layout = styled.div`
  padding: 20px;
  background-color: whitesmoke;
  max-width: calc(100vw - 20px);

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const PageHeader = styled.div`
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-center;
  gap: 12px;
  flex-wrap: wrap;
`

const FiltersContainer = styled.div`
  padding: 20px;
  background: #ffffff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  margin-bottom: 16px;
`

const ResultsCard = styled(Card)`
  .ant-card-body {
    padding: 0;
  }
`

const Title = styled.span`
  font-size: 22px;
  color: #494d50;
  font-weight: 600;
`

const TableWrapper = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 8px;
`

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.7);
    font-weight: 600;
  }

  .ant-table-tbody > tr > td {
    vertical-align: top;
    padding-top: 14px;
    padding-bottom: 14px;
  }
`

const ScopeContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const ScopeTagContainer = styled.span`
  display: inline-flex;
  gap: 4px;
  cursor: default;
`

const ScopeTooltipContent = styled.div`
  max-width: 360px;
  line-height: 1.45;
`

const ScopeGroupTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 2px;
`

const ScopeList = styled.div`
  font-size: 12px;
`

const ScopeHint = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
`

const DiscountValueTag = styled(Tag)`
  margin-right: 0;
  font-weight: 500;
`

const ScopeValueTag = styled(Tag)`
  margin-right: 0;
  font-weight: 500;
`

const VigenciaContainer = styled.div`
  display: grid;
  align-content: start;
  gap: 2px;
`

const VigenciaTag = styled(Tag)`
  width: fit-content;
  margin-right: 0;
  font-size: 12px;
  font-weight: 600;
`

const VigenciaTooltipContent = styled.div`
  display: grid;
  gap: 2px;
  line-height: 1.35;
`

const VigenciaTooltipSecondary = styled.small`
  color: rgba(255, 255, 255, 0.85);
  font-size: 11px;
`

const Store = createSimpleStore(
  {
    cupons: [] as CupomModel[],
    cursosCatalogo: [] as CursoModel[],
    parceiros: [] as ParceirosModel[],
    loading: false,
    filters: {
      vigencia: 'all' as VigenciaFilter,
      cursoIds: [] as number[],
      ufs: [] as string[],
      parceiroId: null as ParceiroFilter,
    },
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
    },
  },
  {
    getCuponsStarted(state) {
      state.loading = true
    },
    getCuponsSuccess(
      state,
      payload: { rows: CupomModel[]; count: number; page: number; limit: number }
    ) {
      state.cupons = payload.rows
      state.pagination.total = payload.count
      state.pagination.page = payload.page
      state.pagination.limit = payload.limit
      state.loading = false
    },
    getCuponsError(state) {
      state.loading = false
    },
    getCursosCatalogoSuccess(state, cursos) {
      state.cursosCatalogo = cursos.rows ?? []
    },
    getParceirosSuccess(state, parceiros) {
      state.parceiros = parceiros ?? []
    },
    setFilters(
      state,
      filters: Partial<{
        vigencia: VigenciaFilter
        cursoIds: number[]
        ufs: string[]
        parceiroId: ParceiroFilter
      }>
    ) {
      state.filters = { ...state.filters, ...filters }
    },
    setPagination(state, pagination: Partial<{ page: number; limit: number; total: number }>) {
      state.pagination = { ...state.pagination, ...pagination }
    },
  },
  {
    thunks: {
      getCupons(
        filters: Partial<{
          vigencia: VigenciaFilter
          cursoIds: number[]
          ufs: string[]
          parceiroId: ParceiroFilter
        }> = {},
        paginationOverride: Partial<{ page: number; limit: number }> = {}
      ) {
        return async (dispatch, getState) => {
          try {
            dispatch(Store.actions.getCuponsStarted())

            const state = getState()
            const effectiveFilters = { ...state.filters, ...filters }
            const effectivePagination = { ...state.pagination, ...paginationOverride }
            const page = effectivePagination.page ?? 1
            const limit = effectivePagination.limit ?? 10
            const offset = (page - 1) * limit

            dispatch(Store.actions.setFilters(effectiveFilters))
            dispatch(Store.actions.setPagination({ page, limit }))

            const params: any = {
              offset,
              limit,
              order: 'DESC',
              orderBy: 'createdAt',
            }

            if (effectiveFilters.vigencia !== 'all') params.vigencia = effectiveFilters.vigencia
            if (effectiveFilters.cursoIds.length)
              params.cursoIds = effectiveFilters.cursoIds.join(',')
            if (effectiveFilters.ufs.length) params.ufs = effectiveFilters.ufs.join(',')
            if (effectiveFilters.parceiroId != null) params.parceiroId = effectiveFilters.parceiroId

            const response = await axios.Cupons.get(params)
            const rows = Array.isArray(response) ? response : (response?.rows ?? [])
            const count = Array.isArray(response)
              ? response.length
              : (response?.count ?? rows.length)

            dispatch(Store.actions.getCuponsSuccess({ rows, count, page, limit }))
          } catch (error: any) {
            const message = error.response?.data?.message ?? 'Ocorreu um erro'
            notification.error({ message })
            dispatch(Store.actions.getCuponsError())
          }
        }
      },
      getCursosCatalogo() {
        return async (dispatch) => {
          try {
            const cursos = await axios.Cursos.get()
            dispatch(Store.actions.getCursosCatalogoSuccess(cursos))
          } catch {
            dispatch(Store.actions.getCursosCatalogoSuccess({ rows: [] }))
          }
        }
      },
      getParceiros() {
        return async (dispatch) => {
          try {
            const parceiros = await axios.Parceiros.list()
            dispatch(Store.actions.getParceirosSuccess(parceiros))
          } catch {
            dispatch(Store.actions.getParceirosSuccess([]))
          }
        }
      },
    },
  }
)
